import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { IPermissionWithShow } from 'src/app/shared/interface';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ILinerOriginService, IOriginServiceCriteria } from '../liner-origin-service.interface';
import * as _ from 'underscore';
import { Api } from 'src/app/shared/constants/api';
import { tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Message } from 'src/app/shared/constants/message';

@Component({
    selector: 'app-liner-origin-service-list',
    templateUrl: './liner-origin-service-list.component.html',
    styleUrls: ['./liner-origin-service-list.component.less'],
    standalone: false
})
export class LinerOriginServiceListComponent implements OnInit,AfterViewInit,OnDestroy {

  constructor(private logger:NGXLogger,private service:DataService,
              private router:Router,private activatedRoute:ActivatedRoute,
              private utilityService:UtilityService,private formBuilder:FormBuilder,
              private permissionService:PermissionService) { }

  totalRecords:any;
  columns=['lineName','consortiumName','originName','polName','destinationName',
           'transhipmentPortNames','mode','deactivated','originStatus','action'];
  pageSizeOptions:number[]=this.utilityService.getPageSizeOptions();
  originServiceDatasource=new MatTableDataSource<ILinerOriginService[]>();
  @ViewChild(MatSort) sort:MatSort;
  @ViewChild(MatPaginator) paginator:MatPaginator;
  originServiceListForm:FormGroup;
  criteria:IOriginServiceCriteria={};
  viewPermission:boolean=false;
  reactivatePermission:boolean=false;
  addPermission:boolean=false;
  editPermission:boolean=false;
  statusTypes:any=[];
  idsToReactivate=[];
	subscription = new Subscription();

  ngOnInit(): void {
    this.getAllPermissions();
    this.initializeForm();
    this.getStatusTypes();
    this.setLineNameValueChangeEvent();
    this.setConsortiumNameValueChangeEvent();
    this.setPolNameValueChangeEvent();
    this.setDestinationNameValueChangeEvent();
    this.setViaDestinationNameValueChangeEvent();
    this.setOriginNameValueChangeEvent();
    this.setOriginStatusValueChangeEvent();
  }

  ngAfterViewInit() {
    this.originServiceDatasource.paginator = this.paginator;
    this.originServiceDatasource.sort = this.sort;
    this.paginator?.page?.pipe(tap(() => this.getOriginServiceList())).subscribe();
    this.getOriginServiceList();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setLineNameValueChangeEvent(){
    this.subscription = this.lineName.valueChanges.subscribe(lineName => {
      this.criteria.lineName=lineName;
      this.getOriginServiceList(true);
    })
  }

  private setConsortiumNameValueChangeEvent(){
    const sub = this.consortiumName.valueChanges.subscribe(consortiumName => {
      this.criteria.consortiumName=consortiumName;
      this.getOriginServiceList(true);
    });
    this.subscription.add(sub);
  }

  private setOriginNameValueChangeEvent(){
    const sub = this.originName.valueChanges.subscribe(originName => {
      this.criteria.originName=originName;
      this.getOriginServiceList(true);
    });
    this.subscription.add(sub);
  }

  private setDestinationNameValueChangeEvent(){
    const sub = this.destinationName.valueChanges.subscribe(destinationName => {
      this.criteria.destinationName=destinationName;
      this.getOriginServiceList(true);
    });
    this.subscription.add(sub);
  }

  private setPolNameValueChangeEvent(){
    const sub = this.polName.valueChanges.subscribe(polName => {
      this.criteria.polName=polName;
      this.getOriginServiceList(true);
    });
    this.subscription.add(sub);
  }

  private setViaDestinationNameValueChangeEvent(){
    const sub = this.viaDestinationName.valueChanges.subscribe(viaDestinationName => {
      this.criteria.viaDestinationName=viaDestinationName;
      this.getOriginServiceList(true);
    });
    this.subscription.add(sub);
  }

  private setOriginStatusValueChangeEvent(){
    const sub = this.originStatus.valueChanges.subscribe(originStatusValue => {
      this.criteria.status=originStatusValue;
      this.getOriginServiceList(true);
    });
    this.subscription.add(sub);
  }

  initializeForm(){
    this.originServiceListForm=this.formBuilder.group({
      lineName:[],
      consortiumName:[],
      originName:[],
      destinationName:[],
      viaDestinationName:[],
      polName:[],
      originStatus:[]
    })
  }
  
  sortData(event:any){
    this.criteria.sort=[event.active,event.direction];
    this.getOriginServiceList(true);
  }

  openOriginServiceForm(id?:number){
    if(id){
      this.editPermission ? this.router.navigate(['edit/'+id],{relativeTo:this.activatedRoute}) :
        this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING,"warning");
    } else{
      this.addPermission ? this.router.navigate(['add'],{relativeTo:this.activatedRoute}) :
        this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING,"warning");
    }
  }

  deactivateChanged(id:number,event:any){
    if(event.checked){
      this.idsToReactivate.push(id);
    } else {
      let index=this.idsToReactivate.indexOf(id);
      this.idsToReactivate.splice(index,1);
    }
    this.logger.info('Ids to reactivate',this.idsToReactivate);
  }

  checkCheckedCondition(id:number){
    return this.idsToReactivate.indexOf(id)>=0;
  }

  activateAllLinerOrigins(){
    if(this.reactivatePermission){
      if(this.idsToReactivate.length>0){
        this.service.post(Api.LINER_ORIGIN_REACTIVATE_GLOBALLY,this.idsToReactivate).subscribe(res => {
          this.utilityService.notifier('Liner Origin Services Reactivated Successfully','success');
          this.getOriginServiceList();
        });
      }
    } else
      this.utilityService.notifier(Message.VIEW_PERMISSION_WARNING,"warning");
  }

  public getAllPermissions(){
    const permissionCodes=[PermissionKey.LINER_ORIGIN_SERVICE_MODULE_ADD_PERMISSION,PermissionKey.LINER_ORIGIN_SERVICE_MODULE_VIEW_PERMISSION,
      PermissionKey.LINER_ORIGIN_SERVICE_MODULE_REACTIVATE_PERMISSION,PermissionKey.LINER_ORIGIN_SERVICE_MODULE_EDIT_PERMISSION];
    const permissionsWithShowValue:IPermissionWithShow[]=this.permissionService.checkPermissions(permissionCodes,PermissionKey.LINER_ORIGIN_SERVICE_MODULE);
    this.addPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.LINER_ORIGIN_SERVICE_MODULE_ADD_PERMISSION})?.show?true:false;
    this.editPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.LINER_ORIGIN_SERVICE_MODULE_EDIT_PERMISSION})?.show?true:false;
    this.viewPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.LINER_ORIGIN_SERVICE_MODULE_VIEW_PERMISSION})?.show?true:false;
    this.reactivatePermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.LINER_ORIGIN_SERVICE_MODULE_REACTIVATE_PERMISSION})?.show?true:false;
  }

  getStatusTypes(){
    this.service.get(Api.STATUS_TYPE_LIST).subscribe(res => {
      this.logger.info('Status Types',res);
      this.statusTypes.push({key: "", label: "Search by Status"});
      this.statusTypes.push(...res);
    });
  }

  getOriginServiceList(resetPageIndex: boolean =false){
    if (resetPageIndex) {
      this.paginator.pageIndex = 0;
    }
    this.criteria.page=this.paginator?.pageIndex;
    this.criteria.size=this.paginator?.pageSize;
    this.criteria.sort= this.criteria.sort? this.criteria.sort : ['createdDate','desc'];
    if(this.viewPermission){
      this.service.get(Api.LINER_ORIGIN_SERVICE,this.criteria).subscribe(res => {
        if(res){
          if(res.contents.length == 0){
            this.utilityService.notifier('No records found','info');
          }
          this.logger.info('Liner Origin Service',res);
          this.originServiceDatasource=res.contents;
          this.totalRecords=res.totalRecords;
          if (this.paginator.pageIndex >= Math.ceil(this.totalRecords / this.paginator.pageSize)) {
            this.paginator.pageIndex = 0;
          }
        }
     });
    } else
        this.utilityService.notifier(Message.VIEW_PERMISSION_WARNING,"warning");
  }

  get originStatus(){ return this.originServiceListForm.get('originStatus'); }
  get lineName(){ return this.originServiceListForm.get('lineName'); }
  get consortiumName(){ return this.originServiceListForm.get('consortiumName'); }
  get originName(){ return this.originServiceListForm.get('originName'); }
  get destinationName(){ return this.originServiceListForm.get('destinationName'); }
  get polName(){ return this.originServiceListForm.get('polName'); }
  get viaDestinationName(){ return this.originServiceListForm.get('viaDestinationName'); }
}
